import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import { LoadingButton } from '@mui/lab';
import { Box, Stack, Typography } from '@mui/material';
import AgentPickerDialog from 'components/private/Modals/operation/AgentPickerDialog';
import LocationPickerDialog from 'components/private/Modals/operation/movement/Workspace/LocationPickerDialog';
import RequireOperationSettings from 'components/shared/RequiredOpertaionSettings/RequireOperationSettings';
import SpinnerLoader from 'components/shared/SpinnerLoader/SpinnerLoader';
import useMovementWorkspace from 'hooks/operation/useMovementWorkspace';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import AgentCard from './AgentCard';
import LocationCard from './LocationCard';
import MovementProductPanel from './MovementProductPanel';
import StatusStepper from './StatusStepper';

const MovementOperationWorkspace = () => {
  const { guid } = useParams();
  const [isAgentDialogOpen, setIsAgentDialogOpen] = useState(false);
  const [locationDirection, setLocationDirection] = useState(null);

  const {
    loading,
    actionLoading,
    workspace,
    title,
    state,
    agent,
    product,
    origin,
    destination,
    canMutate,
    canRunMovement,
    insertAgent,
    updateAgent,
    prepareOrigin,
    prepareDestination,
    runMovement,
    getAvailableAgents,
  } = useMovementWorkspace(guid);

  const handleSelectAgent =
    agent?.guid || agent?.operationAgentGuid ? updateAgent : insertAgent;

  if (loading && !workspace) {
    return (
      <RequireOperationSettings>
        <SpinnerLoader />
      </RequireOperationSettings>
    );
  }

  if (!workspace) {
    return (
      <RequireOperationSettings>
        <Typography color="text.secondary" textAlign="center" py={6}>
          اطلاعات فضای کاری یافت نشد.
        </Typography>
      </RequireOperationSettings>
    );
  }

  return (
    <RequireOperationSettings>
      <Box sx={{ mt: 2, mb: 4, maxWidth: 'lg', mx: 'auto' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          gap={2}
          mb={3}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
            عملیات جابه جایی &quot;{title || workspace?.title}&quot;
          </Typography>

          {canRunMovement ? (
            <LoadingButton
              variant="contained"
              size="large"
              sx={{ gap: 1 }}
              loading={actionLoading}
              onClick={runMovement}
            >
              جابه جایی
              <SwapHorizRoundedIcon />
            </LoadingButton>
          ) : null}
        </Stack>

        <StatusStepper state={state} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg: '240px minmax(260px, 320px) 240px',
            },

            justifyContent: 'space-between',
            alignItems: 'start',
            gap: 3,
          }}
        >
          <LocationCard
            title="مبدا"
            location={origin}
            actionLabel="انتخاب مبدا"
            onAction={() => setLocationDirection('origin')}
            disabled={!canMutate || actionLoading}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <AgentCard
              agent={agent}
              canMutate={canMutate}
              onSelect={() => setIsAgentDialogOpen(true)}
            />
            <MovementProductPanel product={product} />
          </Box>

          <LocationCard
            title="مقصد"
            location={destination}
            actionLabel="انتخاب مقصد"
            onAction={() => setLocationDirection('destination')}
            disabled={!canMutate || actionLoading}
          />
        </Box>
      </Box>

      <AgentPickerDialog
        open={isAgentDialogOpen}
        onClose={() => setIsAgentDialogOpen(false)}
        loading={actionLoading}
        getAvailableAgents={getAvailableAgents}
        onSelectAgent={handleSelectAgent}
        hasSelectedAgent={Boolean(agent)}
      />

      <LocationPickerDialog
        open={Boolean(locationDirection)}
        onClose={() => setLocationDirection(null)}
        direction={locationDirection}
        product={product}
        prepareOrigin={prepareOrigin}
        prepareDestination={prepareDestination}
        loading={actionLoading}
        defaultCount={product?.quantity}
      />
    </RequireOperationSettings>
  );
};

export default MovementOperationWorkspace;
